import { Controller } from "@hotwired/stimulus";
import { scrollBelowHeader } from "~/lib/scroll";

// Course search toolbar of the 2026 redesign (#15087): debounced live search,
// fetch()-based pagination and the tile/list view toggle. Replaces the legacy
// jquery_ujs remote form + courses/search.js.erb plumbing.
export default class extends Controller {
  static targets = [
    "input",
    "results",
    "tilesButton",
    "listButton",
    "clearButton",
    "panel",
    "filterButton",
    "filterLabel",
    "filterCount",
  ];
  // the label values keep the German strings out of the JS (single source
  // in _search.haml; the Vite bundle has no gettext)
  static values = { url: String, labelFiltered: String, labelDefault: String };

  connect() {
    this.onResultsClick = this.onResultsClick.bind(this);
    this.onPopstate = (event) => {
      // entries we registered via Turbolinks are restored by Turbolinks
      if (event.state && event.state.turbolinks) return;
      window.location.reload();
    };
    this.resultsTarget.addEventListener("click", this.onResultsClick);
    window.addEventListener("popstate", this.onPopstate);
    // sessionStorage can throw (private browsing / blocked site data)
    try {
      this.listMode = sessionStorage.getItem("list_state") === "true";
    } catch {
      this.listMode = false;
    }
    this.applyViewMode();
    if (this.hasInputTarget) this.updateClearButton();
    // the state the page was rendered with gets swapped away by the first
    // search before Turbolinks would ever snapshot it
    this.cacheCurrentState();
  }

  disconnect() {
    this.resultsTarget.removeEventListener("click", this.onResultsClick);
    window.removeEventListener("popstate", this.onPopstate);
    clearTimeout(this.debounce);
    if (this.abortController) this.abortController.abort();
    // don't leave the scroll lock behind on turbolinks navigation
    document.body.classList.remove("filter-modal-open");
  }

  search() {
    this.updateClearButton();
    clearTimeout(this.debounce);
    // typing replaces the history entry - one Back press leaves the
    // search instead of stepping through every intermediate query
    this.debounce = setTimeout(
      () => this.load({ page: 1, replace: true }),
      300,
    );
  }

  clear() {
    this.inputTarget.value = "";
    this.updateClearButton();
    clearTimeout(this.debounce);
    this.load({ page: 1, replace: true });
    this.inputTarget.focus();
  }

  // swaps the magnifier for the clear (x) button while a query is active
  updateClearButton() {
    const active = this.inputTarget.value.trim().length > 0;
    this.inputTarget.classList.toggle("has-query", active);
    if (this.hasClearButtonTarget) this.clearButtonTarget.hidden = !active;
  }

  submit(event) {
    event.preventDefault();
    clearTimeout(this.debounce);
    this.load({ page: 1 });
  }

  // mobile only (CSS ignores the class on desktop): the magnifier button
  // expands the search form to full toolbar width
  openSearch() {
    this.element.classList.add("search-open");
    this.inputTarget.focus();
  }

  closeSearch() {
    if (this.inputTarget.value.trim()) this.clear();
    this.element.classList.remove("search-open");
  }

  toggleFilters() {
    if (this.panelTarget.hidden) {
      this.openFilters();
      // desktop only: the panel unfolds inline below the toolbar, so bring it
      // into view. On mobile it is a full-screen modal with a scroll lock -
      // scrolling the page underneath it would only fight that lock.
      if (!this.modal) this.scrollPanelIntoView();
    } else {
      this.closeFilters();
    }
  }

  // keeps the filter button just below the sticky header
  scrollPanelIntoView() {
    scrollBelowHeader(this.filterButtonTarget);
  }

  openFilters() {
    this.panelTarget.hidden = false;
    this.filterButtonTarget.classList.add("open");
    this.filterButtonTarget.setAttribute("aria-expanded", "true");
    // scroll lock while the panel renders as a mobile modal
    document.body.classList.add("filter-modal-open");
    if (this.modal) {
      // dialog semantics only while the panel actually is a modal;
      // on desktop it renders inline and the page stays interactive
      this.panelTarget.setAttribute("role", "dialog");
      this.panelTarget.setAttribute("aria-modal", "true");
      this.panelTarget.querySelector(".filter-close")?.focus();
    }
  }

  closeFilters() {
    // hand focus back before hiding the element that holds it
    if (this.panelTarget.contains(document.activeElement)) {
      this.filterButtonTarget.focus();
    }
    this.panelTarget.hidden = true;
    this.panelTarget.removeAttribute("role");
    this.panelTarget.removeAttribute("aria-modal");
    this.filterButtonTarget.classList.remove("open");
    this.filterButtonTarget.setAttribute("aria-expanded", "false");
    document.body.classList.remove("filter-modal-open");
  }

  // mobile accordion sections of the filter modal
  toggleGroup(event) {
    const group = event.currentTarget.closest(".filter-group");
    group.classList.toggle("open");
    event.currentTarget.setAttribute(
      "aria-expanded",
      String(group.classList.contains("open")),
    );
  }

  // mobile modal: every change applies immediately, so "anwenden" (submit)
  // only has to close the modal there. Keep in sync with $tiles-1col-max.
  get instantApply() {
    return true;
    // We only wanted instant apply on mobile - to not confuse users with smaller screen where the
    // filter box does not fit on the screen- we for now also want that on desktop.
    //
    // @TODO - remove / refactor once long term solution is descided
    //
    // return window.matchMedia("(max-width: 40rem)").matches;
  }

  // whether the panel renders as the full-screen mobile modal - pure CSS,
  // see course-filter-panel.scss. Kept apart from instantApply: that one is
  // forced on for every viewport now, the modal layout is still mobile only.
  // Keep in sync with $tiles-1col-max.
  get modal() {
    return window.matchMedia("(max-width: 40rem)").matches;
  }

  filterChanged() {
    if (!this.instantApply) return;
    this.updateFilterButton();
    this.load({ page: 1 });
  }

  // toggles the "von"/"bis" placeholder of a date field (_filter_panel.haml)
  dateChanged(event) {
    this.syncDatePlaceholder(event.currentTarget);
  }

  // the native picker button is hidden in CSS, so our own calendar icon
  // opens the picker (showPicker() needs a user gesture and is missing in
  // older browsers - focusing the field is the fallback)
  openDatePicker(event) {
    const target = event.currentTarget;
    const input = target.matches("input[type=date]")
      ? target
      : target.closest(".filter-date").querySelector("input[type=date]");
    if (!input) return;
    input.focus();
    if (typeof input.showPicker !== "function") return;
    try {
      input.showPicker();
    } catch {
      // already open / not allowed - the focused field stays typable
    }
  }

  syncDatePlaceholder(input) {
    input.classList.toggle("has-value", input.value !== "");
  }

  applyFilters(event) {
    event.preventDefault();
    this.closeFilters();
    this.updateFilterButton();
    if (!this.instantApply) this.load({ page: 1 });
  }

  resetFilters() {
    this.clearPanelInputs();
    this.closeFilters();
    this.updateFilterButton();
    this.load({ page: 1 });
  }

  // from an active-filter tag: unchecks just that filter and reloads
  removeFilter(event) {
    // the info bubble inside a tag (with_filter_info) only opens its tooltip
    if (event.target.closest("[data-tooltip]")) return;
    const param = event.currentTarget.dataset.filterParam;
    if (param === "date_range") {
      this.clearDateInputs();
    } else {
      const box = this.panelTarget.querySelector(`input[name="${param}"]`);
      if (box) box.checked = false;
    }
    this.updateFilterButton();
    this.load({ page: 1 });
  }

  // form.reset() would restore the server-rendered state, hence explicit
  clearPanelInputs() {
    this.panelInputs("input[type=checkbox]").forEach(
      (input) => (input.checked = false),
    );
    this.clearDateInputs();
  }

  clearDateInputs() {
    this.panelInputs("input[type=date]").forEach((input) => {
      input.value = "";
      this.syncDatePlaceholder(input);
    });
  }

  panelInputs(selector) {
    return Array.from(this.panelTarget.querySelectorAll(selector));
  }

  // active filters as URL params; checked boxes serialize as "true", so a
  // "param=false" (which SolrSearch would treat as active) can never occur
  filterParams() {
    const params = new URLSearchParams();
    if (!this.hasPanelTarget) return params;
    new FormData(this.panelTarget).forEach((value, key) => {
      if (value) params.set(key, value);
    });
    return params;
  }

  updateFilterButton() {
    if (!this.hasFilterButtonTarget) return;
    const count =
      this.panelInputs("input[type=checkbox]:checked").length +
      (this.panelInputs("input[type=date]").some((input) => input.value)
        ? 1
        : 0);
    this.filterButtonTarget.classList.toggle("filtered", count > 0);
    this.filterLabelTarget.textContent =
      count > 0 ? this.labelFilteredValue : this.labelDefaultValue;
    this.filterCountTarget.hidden = count === 0;
    this.filterCountTarget.textContent = count;
  }

  showTiles() {
    this.setListMode(false);
  }

  showList() {
    this.setListMode(true);
  }

  setListMode(on) {
    this.listMode = on;
    // same key the old text-switch used, so the preference carries over;
    // storage can throw in private browsing - the toggle still works then
    try {
      sessionStorage.setItem("list_state", on ? "true" : "false");
    } catch {
      // no persistence, nothing to do
    }
    this.applyViewMode();
  }

  applyViewMode() {
    const container = this.resultsTarget.querySelector(".result-container");
    if (container) container.classList.toggle("list", this.listMode);
    if (this.hasTilesButtonTarget) {
      this.tilesButtonTarget.classList.toggle("active", !this.listMode);
      this.tilesButtonTarget.setAttribute(
        "aria-pressed",
        String(!this.listMode),
      );
    }
    if (this.hasListButtonTarget) {
      this.listButtonTarget.classList.toggle("active", this.listMode);
      this.listButtonTarget.setAttribute("aria-pressed", String(this.listMode));
    }
  }

  // pagination links are re-rendered with every swap, hence the delegation
  onResultsClick(event) {
    const link = event.target.closest(".pagination a");
    if (!link || !this.resultsTarget.contains(link)) return;
    event.preventDefault();
    const page =
      new URL(link.href, window.location.origin).searchParams.get("page") || 1;
    this.load({ page: page, scroll: true });
  }

  load({ page, scroll = false, replace = false }) {
    const params = this.filterParams();
    const q = this.hasInputTarget ? this.inputTarget.value.trim() : "";
    if (q) params.set("q", q);
    if (page && String(page) !== "1") params.set("page", page);
    const query = params.toString();

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.resultsTarget.setAttribute("aria-busy", "true");

    fetch(`${this.urlValue}${query ? `?${query}` : ""}`, {
      // fetch() doesn't send this header on its own; CoursesController#search
      // keys on it via request.xhr? to render the fragment instead of redirecting
      headers: { "X-Requested-With": "XMLHttpRequest" },
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        this.resultsTarget.innerHTML = html;
        this.resultsTarget.removeAttribute("aria-busy");
        this.applyViewMode();
        this.pushHistory(
          `${window.location.pathname}${query ? `?${query}` : ""}`,
          replace,
        );
        this.cacheCurrentState();
        if (scroll) this.element.scrollIntoView({ behavior: "smooth" });
        this.reinitWidgets();
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        // fall back to a full navigation so the user still gets results
        window.location.assign(
          `${window.location.pathname}${query ? `?${query}` : ""}`,
        );
      });
  }

  pushHistory(url, replace = false) {
    // plain pushState entries are ignored by Turbolinks' popstate handler,
    // breaking Back from a detail page; registering them with Turbolinks
    // makes it restore/refetch the searched state instead. Typing-triggered
    // loads replace instead of push, so Back never walks intermediate queries.
    if (window.Turbolinks) {
      const controller = window.Turbolinks.controller;
      const method = replace
        ? "replaceHistoryWithLocationAndRestorationIdentifier"
        : "pushHistoryWithLocationAndRestorationIdentifier";
      controller[method](url, window.Turbolinks.uuid());
      // Turbolinks files the snapshot it takes when leaving the page under
      // the last URL it rendered itself. Without this, the swapped state was
      // cached under the previous search URL and Back to it refetched the
      // page from the server, jumping the scroll position after the render.
      controller.lastRenderedLocation = controller.location;
    } else if (replace) {
      history.replaceState({}, "", url);
    } else {
      history.pushState({}, "", url);
    }
  }

  // Turbolinks only snapshots a page when it is left, so the in-place states
  // in between would have no snapshot and Back to them meant a refetch plus
  // scroll jump. Snapshot each state under its own URL right after the swap,
  // while the filter inputs still match the results (a snapshot taken at the
  // start of the next load would pair new checkboxes with old results).
  // Turbolinks' own cacheSnapshot() clones the live body deferred - hence
  // the sync clone.
  cacheCurrentState() {
    if (!window.Turbolinks) return;
    const controller = window.Turbolinks.controller;
    if (!controller.shouldCacheSnapshot()) return;
    controller.notifyApplicationBeforeCachingSnapshot();
    controller.cache.put(
      controller.location,
      controller.view.getSnapshot().clone(),
    );
  }

  reinitWidgets() {
    // scoped event instead of a synthetic turbolinks:load: re-firing all
    // global initializers leaked a document click listener per search
    // (courses.js binds unconditionally). Only watch_list.js needs to
    // re-bind the swapped tiles; cart buttons are Stimulus and reconnect
    // on their own. Foundation tooltips need an explicit init.
    document.dispatchEvent(new Event("cip:results-replaced"));
    if (window.$ && window.$.fn.foundation)
      window.$(this.resultsTarget).foundation();
  }
}
